import { Tooltip as MuiTooltip, type TooltipProps as MuiTooltipProps } from "@mui/material";
import type { ReactNode } from "react";

type TooltipAlign = "start" | "center" | "end";
type TooltipSide = "top" | "bottom";

type TooltipProps = {
  /** Optional leading icon rendered at the top of the tooltip — used for the
   *  chip-style layout (e.g. data-source chips, search-summary tags). */
  icon?: ReactNode;
  title?: string;
  description?: ReactNode;
  align?: TooltipAlign;
  /** Which side of the trigger the tooltip opens on. Defaults to "bottom". */
  side?: TooltipSide;
  width?: number | string;
  /** Extra classes for the trigger's wrapper span — e.g. "min-w-0 truncate"
   *  when the trigger sits in a shrinking flex/grid cell: the wrapper below is
   *  itself the flex item, so its OWN min-width:auto is what stops a long
   *  nowrap child from shrinking (a truncate class on the child alone isn't
   *  enough — the child never gets narrower than the wrapper lets it). */
  wrapperClassName?: string;
  children: ReactNode;
};

function alignToPlacement(a: TooltipAlign, side: TooltipSide): MuiTooltipProps["placement"] {
  if (a === "start") return `${side}-start`;
  if (a === "end") return `${side}-end`;
  return side;
}

/**
 * Brand tooltip — wraps `@mui/material/Tooltip` (Popper-based positioning) but
 * renders our custom dark-card body in the title slot: optional icon row,
 * optional title in white, optional description in grey-300. Keeps the prior
 * `icon` / `title` / `description` / `align` / `width` consumer API.
 *
 * Behavioral differences vs. the prior hand-rolled tooltip:
 * - Positioning is now Popper-managed (auto-flip near viewport edges).
 * - `children` must accept refs (DOM nodes / forwardRef components — every
 *   current call site qualifies; new call sites with custom function-component
 *   children may need a `<span>` wrapper).
 */
export function Tooltip({
  icon,
  title,
  description,
  align = "center",
  side = "bottom",
  width = 240,
  wrapperClassName,
  children,
}: TooltipProps) {
  if (!title && !description && !icon) {
    return <>{children}</>;
  }

  const body = (
    <span
      className="flex flex-col gap-1 rounded-[4px] bg-grey-950 px-4 py-3 text-grey-300"
      style={{ width }}
    >
      {(icon || title) && (
        <span className="flex items-center gap-2">
          {icon}
          {title && (
            <span
              className={[
                "font-medium leading-snug text-white",
                icon ? "text-body" : "text-small",
              ].join(" ")}
            >
              {title}
            </span>
          )}
        </span>
      )}
      {description && (
        <span className="text-small font-normal leading-snug whitespace-pre-line text-grey-300">
          {description}
        </span>
      )}
    </span>
  );

  return (
    <MuiTooltip
      title={body}
      placement={alignToPlacement(align, side)}
      enterDelay={0}
      leaveDelay={0}
      slotProps={{
        tooltip: {
          // Strip MUI's default padding/background — our body owns it.
          sx: {
            backgroundColor: "transparent",
            padding: 0,
            maxWidth: "none",
            boxShadow: "0px 8px 32px #2d343c14", // shadow-soft
          },
        },
        popper: {
          modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
        },
      }}
    >
      <span className={["inline-flex", wrapperClassName].filter(Boolean).join(" ")}>
        {children}
      </span>
    </MuiTooltip>
  );
}
